import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '@/contexts/DataContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { InfoIcon, ArrowLeft, ArrowRight, PlusCircle, X, Maximize2, Minimize2 } from 'lucide-react';

export default function PitchCreateGTM() {
  const { saveTempPitch, getTempPitch } = useData();
  const navigate = useNavigate();
  const [fullscreen, setFullscreen] = useState(false);
  const [newChannel, setNewChannel] = useState('');
  const [newPartner, setNewPartner] = useState('');
  
  const [form, setForm] = useState(() => {
    const saved = getTempPitch();
    return {
      gtm: saved?.gtm || {
        customerChannels: [],
        salesStrategy: '',
        marketingPlan: '',
        partnerships: [],
        targetEarlyAdopters: '',
      }
    };
  });

  useEffect(() => {
    saveTempPitch(form);
  }, [form, saveTempPitch]);

  const handleBack = () => {
    navigate('/pitch/new/financial');
  };

  const handleNext = () => {
    navigate('/pitch/new/ip');
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
        setFullscreen(false);
      }
    }
  };

  const addArrayItem = (field: string, value: string) => {
    if (!value.trim()) return;
    setForm(prev => ({
      ...prev,
      gtm: {
        ...prev.gtm,
        [field]: [...prev.gtm[field as keyof typeof prev.gtm] as string[], value.trim()]
      }
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setForm(prev => ({
      ...prev,
      gtm: {
        ...prev.gtm,
        [field]: (prev.gtm[field as keyof typeof prev.gtm] as string[]).filter((_, i) => i !== index)
      }
    }));
  };

  return (
    <div className={`${fullscreen ? 'fixed inset-0 z-50 bg-background overflow-auto p-8' : ''}`}>
      <div className="max-w-4xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Go-to-Market Strategy</h1>
            <p className="text-muted-foreground mt-1">Step 5 of 8 · How will you reach customers?</p>
          </div>
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Customer Acquisition</CardTitle>
            <CardDescription>
              A great product doesn't sell itself. How will you get your first 100 customers?
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-8">
            <Alert className="bg-orange-50 border-orange-200">
              <InfoIcon className="h-4 w-4 text-orange-600" />
              <AlertDescription className="text-orange-700">
                "If you build it, they will come" is not a strategy. Be specific about your acquisition channels.
              </AlertDescription>
            </Alert>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Customer Acquisition Channels</Label>
              <div className="flex gap-2">
                <Input
                  value={newChannel}
                  onChange={e => setNewChannel(e.target.value)}
                  placeholder="e.g., Content Marketing, Paid Ads, Sales Team, Referrals"
                  className="text-base py-6"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addArrayItem('customerChannels', newChannel);
                      setNewChannel('');
                    }
                  }}
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('customerChannels', newChannel);
                    setNewChannel('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.gtm.customerChannels.map((channel, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{channel}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('customerChannels', i)}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Sales Strategy</Label>
              <Textarea
                value={form.gtm.salesStrategy}
                onChange={e => setForm({
                  ...form,
                  gtm: {
                    ...form.gtm,
                    salesStrategy: e.target.value
                  }
                })}
                placeholder="Self-serve? Enterprise sales? Channel partners? Inside sales team?"
                className="min-h-[120px] text-base p-4"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Marketing Plan</Label>
              <Textarea
                value={form.gtm.marketingPlan}
                onChange={e => setForm({
                  ...form,
                  gtm: {
                    ...form.gtm,
                    marketingPlan: e.target.value
                  }
                })}
                placeholder="Content marketing, SEO, PR, Events, Webinars, Community building..."
                className="min-h-[120px] text-base p-4"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Target Early Adopters</Label>
              <Textarea
                value={form.gtm.targetEarlyAdopters}
                onChange={e => setForm({
                  ...form,
                  gtm: {
                    ...form.gtm,
                    targetEarlyAdopters: e.target.value
                  }
                })}
                placeholder="Who will you target first? Why them? How will you reach them?"
                className="min-h-[120px] text-base p-4"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-lg font-medium">Key Partnerships</Label>
              <div className="flex gap-2">
                <Input
                  value={newPartner}
                  onChange={e => setNewPartner(e.target.value)}
                  placeholder="Add a strategic partner"
                  className="text-base py-6"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      addArrayItem('partnerships', newPartner);
                      setNewPartner('');
                    }
                  }}
                />
                <Button 
                  type="button" 
                  onClick={() => {
                    addArrayItem('partnerships', newPartner);
                    setNewPartner('');
                  }}
                  className="px-6"
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {form.gtm.partnerships.map((partner, i) => (
                  <div key={i} className="flex items-center gap-1 bg-secondary px-3 py-2 rounded-md">
                    <span className="text-sm">{partner}</span>
                    <X 
                      className="h-4 w-4 cursor-pointer hover:text-red-500" 
                      onClick={() => removeArrayItem('partnerships', i)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-between pt-6">
            <Button onClick={handleBack} variant="outline" size="lg" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back: Financial
            </Button>
            <Button onClick={handleNext} size="lg" className="gap-2">
              Next: IP & Technology
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}